import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import OrderService from '../services/OrderService';
import { SupplierOrder } from '../types';
import globalErrorHandler from '../../../core/services/GlobalErrorHandler';
import { HttpError } from '../../../core/services/HttpService';
import { OrderListView } from './OrderListView';
import { DraftView, SentView, CatalogPanel } from './OrderSentView';
import { ReceiveView } from './OrderReceiveView';
import styles from './OrdersPage.module.css';

const COLORS = {
  muted: '#64748b',
  border: '#e0e3ef',
};

type LeftPanelMode = 'list' | 'catalog';

function EmptyDetailPlaceholder(): React.JSX.Element {
  return (
    <div className={styles.emptyDetail}>
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={COLORS.border} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
        <line x1="8" y1="21" x2="16" y2="21" />
        <line x1="12" y1="17" x2="12" y2="21" />
      </svg>
      <span className={styles.emptyDetailLabel}>Select an order to view details</span>
    </div>
  );
}

export default function OrdersPage(): React.JSX.Element {
  const [orders, setOrders] = useState<SupplierOrder[]>([]);
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);
  const [orderDetail, setOrderDetail] = useState<SupplierOrder | null>(null);
  const [leftPanelMode, setLeftPanelMode] = useState<LeftPanelMode>('list');
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const draftOrders = useMemo(() => orders.filter((o) => o.status === 'draft'), [orders]);
  const sentOrders = useMemo(() => orders.filter((o) => o.status === 'sent'), [orders]);
  const deliveredOrders = useMemo(() => orders.filter((o) => o.status === 'delivered'), [orders]);

  const workAreaName = useMemo(() => {
    if (orders.length > 0) return orders[0].work_area_name;
    return '';
  }, [orders]);

  const fetchOrders = useCallback(async () => {
    setLoadingOrders(true);
    try {
      const data = await OrderService.getOrders();
      setOrders(data);
    } catch (err) { if (err instanceof HttpError) globalErrorHandler.handle(err); }
    setLoadingOrders(false);
  }, []);

  const fetchDetail = useCallback(async (orderId: number) => {
    setLoadingDetail(true);
    try {
      const data = await OrderService.getOrderDetail(orderId);
      setOrderDetail(data);
    } catch (err) { if (err instanceof HttpError) globalErrorHandler.handle(err); }
    setLoadingDetail(false);
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  useEffect(() => {
    if (selectedOrderId !== null) {
      fetchDetail(selectedOrderId);
    } else {
      setOrderDetail(null);
    }
  }, [selectedOrderId, fetchDetail]);

  const handleSelectOrder = (orderId: number) => {
    setSelectedOrderId(orderId);
    setLeftPanelMode('list');
  };

  const handleOrderStatusChange = useCallback(() => {
    setSelectedOrderId(null);
    setOrderDetail(null);
    setLeftPanelMode('list');
    fetchOrders();
  }, [fetchOrders]);

  const handleRefreshDetail = useCallback(() => {
    if (selectedOrderId !== null) {
      fetchDetail(selectedOrderId);
      fetchOrders();
    }
  }, [selectedOrderId, fetchDetail, fetchOrders]);

  const handleAddItemMode = useCallback(() => {
    setLeftPanelMode('catalog');
  }, []);

  const handleCatalogBack = useCallback(() => {
    setLeftPanelMode('list');
  }, []);

  const handleCatalogItemAdded = useCallback(() => {
    if (selectedOrderId !== null) {
      fetchDetail(selectedOrderId);
      fetchOrders();
    }
  }, [selectedOrderId, fetchDetail, fetchOrders]);

  const renderDetailView = () => {
    if (loadingDetail) {
      return (
        <div className={styles.emptyDetail}>Loading...</div>
      );
    }

    if (!orderDetail) return <EmptyDetailPlaceholder />;

    switch (orderDetail.status) {
      case 'draft':
        return (
          <DraftView
            order={orderDetail}
            onSend={handleOrderStatusChange}
            onRefreshDetail={handleRefreshDetail}
            onAddItemMode={handleAddItemMode}
          />
        );
      case 'sent':
        return (
          <SentView
            order={orderDetail}
            onMarkDelivered={handleOrderStatusChange}
          />
        );
      case 'delivered':
        return (
          <ReceiveView
            order={orderDetail}
            onReceive={handleOrderStatusChange}
          />
        );
      default:
        return <EmptyDetailPlaceholder />;
    }
  };

  return (
    <div className={styles.pageContainer}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h1 className={styles.headerTitle}>Orders</h1>
          {workAreaName && (
            <span className={styles.headerWorkArea}>{workAreaName}</span>
          )}
        </div>
        <div className={styles.headerBadges}>
          {draftOrders.length > 0 && (
            <span className={styles.draftBadge}>
              {draftOrders.length} draft{draftOrders.length !== 1 ? 's' : ''}
            </span>
          )}
          {deliveredOrders.length > 0 && (
            <span className={styles.deliveryBadge}>
              {deliveredOrders.length} delivery pending
            </span>
          )}
        </div>
      </div>

      <div className={styles.gridLayout}>
        <div className={styles.leftPanel}>
          <AnimatePresence mode="wait">
            {leftPanelMode === 'list' ? (
              <motion.div
                key="order-list"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
                className={styles.motionContainer}
              >
                <OrderListView
                  draftOrders={draftOrders}
                  sentOrders={sentOrders}
                  deliveredOrders={deliveredOrders}
                  selectedOrderId={selectedOrderId}
                  loadingOrders={loadingOrders}
                  totalOrders={orders.length}
                  onSelectOrder={handleSelectOrder}
                />
              </motion.div>
            ) : (
              <motion.div
                key="catalog"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.2 }}
                className={styles.motionContainer}
              >
                {orderDetail && (
                  <CatalogPanel
                    order={orderDetail}
                    onBack={handleCatalogBack}
                    onItemAdded={handleCatalogItemAdded}
                  />
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className={styles.rightPanel}>
          <AnimatePresence mode="wait">
            <motion.div
              key={selectedOrderId ?? 'empty'}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className={styles.motionContainer}
            >
              {renderDetailView()}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
